import React from 'react';
import { Location, Bus, Fin, Start, Walk } from '../Icons/CustomIcons';

const formatTime = (iso) =>
  iso ? new Date(iso).toLocaleString('de-CH', { hour: '2-digit', minute: '2-digit' }) : '—';

const formatDistance = (meters) =>
  meters >= 1000 ? `${(meters / 1000).toFixed(1).replace('.', ',')} km` : `${Math.round(meters / 10) * 10} m`;

const splitAddress = (address) => {
  if (!address) return { street: address, cityLine: null };
  const commaIndex = address.indexOf(',');
  if (commaIndex === -1) return { street: address, cityLine: null };
  return {
    street: address.slice(0, commaIndex).trim(),
    cityLine: address.slice(commaIndex + 1).trim(),
  };
};

const OeVConnectionDisplay = ({ travelLoading, travelError, travelPlan, isCoordinateOnlyLocation, sbbHref }) => {
  const fromAddr = splitAddress(travelPlan?.from_address);
  const destAddr = splitAddress(travelPlan?.destination_address);

  // Ankunft am Ziel = Ankunft an der letzten Haltestelle + Fussweg zum Ziel.
  // travelPlan.arrival ist nur die ÖV-Ankunft an der Haltestelle. Bevorzugt wird
  // destination_arrival (Trip-EndTime der ÖV-Auskunft, inkl. Fussweg); fehlt sie,
  // wird der separat berechnete Fussweg (walking_to_destination) addiert.
  const walkSeconds = travelPlan?.walking_to_destination?.duration_s ?? 0;
  const destinationArrival = travelPlan?.destination_arrival
    ? new Date(travelPlan.destination_arrival)
    : travelPlan?.arrival
      ? new Date(new Date(travelPlan.arrival).getTime() + walkSeconds * 1000)
      : null;

  // Startadresse = Zieladresse: das Backend liefert keine Verbindung, sondern
  // nur den Hinweis, dass man bereits da ist.
  const atDestination = travelPlan?.at_destination === true;
  // Reiner Fussweg: Die ÖV-Auskunft empfiehlt zu gehen, weil das schneller ist
  // als jede Verbindung. Es gibt keine Haltestellen zu zeigen.
  const walkOnly = travelPlan?.walk_only === true;
  // Fussweg von der Startadresse zur Einstiegshaltestelle (erstes Bein des Trips)
  const walkToStation = travelPlan?.walk_to_station_minutes ?? 0;
  return (
    <div className="anreise-container">

      {travelLoading && <p>Verbindung wird gesucht...</p>}

      {travelError && (
        <p style={{ color: '#d32f2f' }}>{travelError}</p>
      )}

      {atDestination && !travelLoading && (
        <div className="anreise-at-destination">
          <div className="timeline-icon">
            <Fin size={20} />
          </div>
          <p className="Font-M-I-130-T Color-Black">Sie befinden sich am Ziel.</p>
        </div>
      )}

      {walkOnly && !travelLoading && (
        <>
          <div className="anreise-oev-header">
            <div className="anreise-oev-header-text">
              <h2 className="Font-L-S-130-T Colors-Black">Anreise zu Fuss</h2>
              <p className="Font-S-B-130-T Colors-Grey-06">Zu Fuss schneller als mit dem ÖV</p>
            </div>
          </div>
          <div className="anreise-at-destination">
            <div className="timeline-icon">
              <Walk size={20} />
            </div>
            <p className="Font-M-I-130-T Color-Black">
              Das Ziel liegt etwa {formatDistance(travelPlan.distance_m ?? 0)} von Ihrer Startadresse entfernt –
              zu Fuss in ca. <strong>{travelPlan.duration_text}</strong>
            </p>
          </div>
        </>
      )}

      {travelPlan && !atDestination && !walkOnly && !travelLoading && (
        <>
          {/* Header */}
          <div className="anreise-oev-header">
            <div className="anreise-oev-header-text">
              <h2 className="Font-L-S-130-T Colors-Black">Anreise mit ÖV</h2>
              <p className="Font-S-B-130-T Colors-Grey-06">Empfohlene Verbindung und Dauer</p>
            </div>
            <span className="Font-S-S-130-T Colors-White anreise-oev-header-transfers">Umstiege: {travelPlan.transfers}</span>          
          </div>

          {/* Timeline */}
          <div className="timeline">

          {/* Startadresse */}
          {travelPlan.from_address && (
            <div className="timeline-item">
              <div className="timeline-icon">
                <Location size={20} />
              </div>
              <div className="timeline-line" />
              <div className="timeline-content">
                <span className="Font-S-B-130-T Colors-Grey-06">Startadresse</span>
                <p className="Font-M-I-130-T Color-Black">
                  <span className="card-address-span">{fromAddr.street} </span>
                  {fromAddr.cityLine && <span className="card-address-span" style={{ whiteSpace: 'nowrap' }}>{fromAddr.cityLine}</span>}
                </p>
                <p className="timeline-meta Font-S-B-130-T Colors-Grey-06">Ihr Standort</p>
              </div>
            </div>
          )}

          {/* Von (Einstiegshaltestelle) */}
          <div className="timeline-item">
            <div className="timeline-icon">
              <Location size={20} />
            </div>
            <div className="timeline-line" />
            <div className="timeline-content">
              <span className="Font-S-B-130-T Colors-Grey-06">Einsteigen</span>
              <p className="Font-M-I-130-T Color-Black">{travelPlan.from_station}</p>
              <p className="timeline-meta Font-S-B-130-T Colors-Grey-06">
                Haltestelle
                <span className="timeline-divider">|</span>
                Abfahrt: <strong>{formatTime(travelPlan.departure)}</strong>
              </p>
              {walkToStation > 0 && (
                <p className="timeline-meta Font-S-B-130-T Colors-Grey-06">
                  Fussweg zur Haltestelle: {walkToStation} Min.
                  <span className="timeline-divider">|</span>
                  Abmarsch: <strong>{formatTime(travelPlan.trip_start)}</strong>
                </p>
              )}
            </div>
          </div>

          {/* Nach */}
          <div className="timeline-item">
            <div className="timeline-icon">
              <Start size={20} />
            </div>
            <div className="timeline-line" />
            <div>
              <div className="timeline-content">
                <span className="Font-S-B-130-T Colors-Grey-06">Nach</span>
                <p className="Font-M-I-130-T Color-Black">{travelPlan.to_station}</p>
                <p className="timeline-meta Font-S-B-130-T Colors-Grey-06">
                  Haltestelle
                  <span className="timeline-divider">|</span>
                  Ankunft: <strong>{formatTime(travelPlan.arrival)}</strong>
                </p>
              </div>
              {/* Dauer-Badge */}
              <div className="Font-S-S-130-T Colors-White travel-duration-badge">
                <Bus size={20} color="var(--Colors-White)" />
                <span>{travelPlan.duration_text} Dauer</span>
              </div>
            </div>
          </div>

          {/* Ziel */}
          <div className="timeline-item timeline-item--last">
            <div className="timeline-icon">
              <Fin size={20} />
            </div>
            <div className="timeline-line"/>
            <div className="timeline-content timeline-content--last">
              <span className="Font-S-B-130-T Colors-Grey-06">Ziel</span>
              <p className="Font-M-I-130-T Color-Black">
                <span className="card-address-span">{destAddr.street} </span>
                {destAddr.cityLine && <span className="card-address-span" style={{ whiteSpace: 'nowrap' }}>{destAddr.cityLine}</span>}
              </p>
              <p className="timeline-meta Font-S-B-130-T Colors-Grey-06">
                Zieladresse
                <span className="timeline-divider">|</span>
                Ankunft: <strong>{destinationArrival ? destinationArrival.toLocaleString('de-CH', { hour: '2-digit', minute: '2-digit' }) : '—'}</strong>
              </p>
            </div>
          </div>
        </div>

        </>
      )}
    </div>
  );
};

export { OeVConnectionDisplay };
